/* eslint-disable camelcase */
/* eslint-disable import/prefer-default-export */
import { gql } from '@apollo/client';
import { camelizeKeys } from 'humps';
import { closeModal } from 'actions/modals';
import { sendEvent, sendTechEvent } from 'services/analytics';
import { refreshUser } from 'actions/user/refreshUser';
import { isIosApp } from 'services/environment';
import {
  IN_APP_PAYMENT_INIT,
  IN_APP_PAYMENT_READY,
  IN_APP_PAYMENT_ERROR,
  IN_APP_PAYMENT_PRODUCT_UPDATED,
  IN_APP_PAYMENT_PURCHASE_START,
  IN_APP_PAYMENT_PURCHASE_ERROR,
  IN_APP_PAYMENT_PURCHASE_COMPLETE,
  IN_APP_PAYMENT_PURCHASE_CANCELLED,
  IN_APP_PAYMENT_RESTORE_START,
  IN_APP_PAYMENT_RESTORE_COMPLETE,
  IN_APP_PAYMENT_RESTORE_ERROR,
  IN_APP_PAYMENT_CLEAR_ERRORS,
} from '../constants';

export const GET_CONFIG_GQL = gql`
  query GetInAppPaymentConfig($platform: String!) {
    SubscriptionPlanConfig(platform: $platform) {
      id
      log_verbosity
      product_ids
    }
  }
`;

const PROCESS_TRANSACTION_GQL = gql`
  mutation ProcessTransaction(
    $appStoreReceipt: String
    $currency: String
    $id: String
    $introPriceMicros: Float
    $introPricePaymentMode: SubscriptionPaymentMode
    $introPricePeriod: Int
    $introPricePeriodUnit: SubscriptionPeriod
    $originalTransactionId: String
    $priceMicros: Float
    $subscriptionPlanId: String
    $type: String
    $discount: DiscountOffer
    $retentionOffer: Boolean
  ) {
    ProcessTransaction(
      appStoreReceipt: $appStoreReceipt
      currency: $currency
      id: $id
      introPriceMicrosF: $introPriceMicros
      introPricePaymentMode: $introPricePaymentMode
      introPricePeriod: $introPricePeriod
      introPricePeriodUnit: $introPricePeriodUnit
      originalTransactionId: $originalTransactionId
      priceMicrosF: $priceMicros
      subscriptionPlanId: $subscriptionPlanId
      type: $type
      discount: $discount
      retentionOffer: $retentionOffer
    ) {
      success
      errorCode
      errorMessage
      isNewSubscription
    }
  }
`;

const handlers = {};
let initialRefreshTriggered = false;

export function init() {
  if (!window.CdvPurchase) {
    console.error('window.CdvPurchase is not defined');
    return;
  }

  const { store: paymentStore, Platform, ErrorCode, ProductType } = window.CdvPurchase;

  console.info('window.CdvPurchase == ', window.CdvPurchase);
  // eslint-disable-next-line consistent-return
  return async (dispatch, getState, { client }) => {
    const {
      user,
      inAppPayment: { ready },
    } = getState();

    if (user.logged_in && paymentStore && !ready) {
      const configResponse = await client.query({
        query: GET_CONFIG_GQL,
        variables: {
          platform: window.cordova.platformId,
        },
        fetchPolicy: 'network-only',
      });

      if (!Array.isArray(configResponse?.data?.SubscriptionPlanConfig?.product_ids)) {
        dispatch({
          type: IN_APP_PAYMENT_ERROR,
          payload: {
            error: {
              message:
                'In-App subscriptions are not available. Please check the website for more info',
              code: 'NOT_CONFIGURED',
            },
          },
        });
        return;
      }

      // Set our log level
      paymentStore.verbosity =
        configResponse.data.SubscriptionPlanConfig.log_verbosity || paymentStore.INFO;

      // Configure the products that we are going to sell. I assume this needs to include any product
      // that has ever been sold via the app so that we will receive transactions for payments, cancellations, etc
      // for users who have products that may no longer be for sale (we should test this, but for now we only have 2 products anyway).
      const productObjects = configResponse.data.SubscriptionPlanConfig.product_ids.map(id => ({
        id,
        platform:
          window.cordova.platformId === 'ios' ? Platform.APPLE_APPSTORE : Platform.GOOGLE_PLAY,
        type: paymentStore.PAID_SUBSCRIPTION,
      }));
      // dispatch({ type: IN_APP_PAYMENT_INIT, payload: productObjects });
      paymentStore.register(productObjects);

      // Configure the validator.
      // This is based on https://github.com/j3k0/cordova-plugin-purchase/blob/master/src/js/validator.js.
      // We use a callback rather than the direct method so that we can leverage our existing
      // GraphQL API with authentication, logging, rate limiting, etc.
      let validationRequests = [];
      let validationTimeout = null;
      const runValidation = async () => {
        validationTimeout = null;
        const requests = validationRequests;
        validationRequests = [];

        // Merge validation requests by products.
        const byProduct = {};
        requests.forEach(request => {
          const productId = request.product.id;
          if (byProduct[productId]) {
            byProduct[productId].callbacks.push(request.callback);
            // assume the most up to date value for product will come last
            byProduct[productId].product = request.product;
          } else {
            byProduct[productId] = {
              product: request.product,
              callbacks: [request.callback],
            };
          }
        });

        // Run one validation request for each product.
        Object.keys(byProduct).forEach(async productId => {
          const request = byProduct[productId];
          const { product } = request;
          try {
            // Find out which discount was used so we can store the price paid
            let discount;
            if (product?.additionalData?.discount?.id) {
              discount = product.discounts.find(
                _discount => _discount.id === product.additionalData.discount.id,
              );
            }
            const { data } = await client.mutate({
              mutation: PROCESS_TRANSACTION_GQL,
              variables: camelizeKeys({
                appStoreReceipt: product.transaction.appStoreReceipt || product.transaction.receipt,
                id: product.transaction.id,
                originalTransactionId: product.transaction.originalTransactionId,
                subscriptionPlanId: product.id,
                type: product.transaction.type,
                currency: product.currency || undefined,
                priceMicros: product.priceMicros || undefined,
                introPriceMicros: product.introPriceMicros || undefined,
                introPricePeriod: product.introPricePeriod || undefined,
                introPricePeriodUnit: product.introPricePeriodUnit || undefined,
                introPricePaymentMode: product.introPricePaymentMode || undefined,
                discount,
                retentionOffer: product.additionalData?.retentionOffer === true,
              }),
            });
            const validationResult = data.ProcessTransaction;
            if (validationResult.isNewSubscription) {
              // The server definitively tells us whether a new subscription was created as a result of this transaction.
              // This seems more reliable than comparing transaction_id with original_transaction_id, where there are various
              // scenarios why these may differ.
              sendEvent({
                event: 'InAppPayment.NewSubscription',
                provider: paymentStore.platform,
                ext_subscription_id:
                  product.transaction.originalTransactionId || product.transaction.id,
                subscription_plan_id: product.id,
                product: {
                  ...product,
                  transaction: null,
                },
              });
              if (window.intercom) {
                setTimeout(
                  () =>
                    window.intercom.logEvent('in-app-subscription', {
                      provider: paymentStore.platform,
                      ext_subscription_id:
                        product.transaction.original_transaction_id || product.transaction.id,
                      subscription_plan_id: product.id,
                      currency: product.currency,
                      price: product.price,
                    }),
                  1000,
                );
              }
            }

            const callbackPayload = validationResult.success
              ? product
              : {
                code: validationResult.errorCode,
                error: { message: validationResult.errorMessage },
              };
            request.callbacks.forEach(callback => {
              callback(validationResult.success, callbackPayload);
            });
          } catch (err) {
            console.warn('Failed to validate receipt', err);
            dispatch({
              type: IN_APP_PAYMENT_ERROR,
              payload: { error: { message: err.message, code: 'VERIFY_FAIL' } },
            });
            request.callbacks.forEach(callback => {
              callback(false, err);
            });
            sendTechEvent('iap.receiptValidationFail');
          }
        });
      };

      paymentStore.validator = async (product, callback) => {
        // Filter out the iOS application receipt. We are a free download so don't really care about validating this.
        if (product.type === 'application') {
          callback(true, product);
          return;
        }

        // Batch the requests rather than send every receipt to the server.
        validationRequests.push({ product, callback });
        if (validationTimeout) {
          clearTimeout(validationTimeout);
        }
        validationTimeout = setTimeout(runValidation, 500);
      };

      // Configure event handlers.
      handlers.updated = product => {
        if (product.platform.includes('ios')) {
          console.info(product.platform, ' handlers.updated = ', product.raw, product);
          dispatch({ type: IN_APP_PAYMENT_PRODUCT_UPDATED, payload: product.raw });
          return;
        }

        const lastOffer = product?.offers[product?.offers?.length - 1];
        const pricing = lastOffer?.pricingPhases[lastOffer?.pricingPhases?.length - 1];
        const productPayload = {
          id: product.id,
          ...pricing,
        };
        console.info('handlers.updated = ', product, productPayload);
        // Called when any subscription product is updated
        dispatch({ type: IN_APP_PAYMENT_PRODUCT_UPDATED, payload: productPayload });
      };

      handlers.error = error => {
        switch (error.code) {
          case ErrorCode.REFRESH:
            dispatch({ type: IN_APP_PAYMENT_RESTORE_ERROR, payload: error });
            break;

          case ErrorCode.PAYMENT_EXPIRED:
          case ErrorCode.PAYMENT_CANCELLED:
            dispatch({ type: IN_APP_PAYMENT_CLEAR_ERRORS });
            break;

          default:
            console.error(`ERROR ${error.code}: ${error.message}`);
            dispatch({ type: IN_APP_PAYMENT_ERROR, payload: { error } });
            sendTechEvent('iap.error', { code: error.code });
        }
      };

      handlers.approved = product => {
        product.verify().then(() => {
          product.finish();
          handlers.verified();
        });
      };

      handlers.verified = async product => {
        if (product) {
          console.info('payment verified', JSON.stringify(product));
          product.finish();
        }

        dispatch(closeModal());
        await dispatch(refreshUser());
        sendTechEvent('iap.complete');
      };

      handlers.unverified = async product => {
        console.info('payment unverified', JSON.stringify(product));
        await dispatch(refreshUser());
        dispatch({
          type: IN_APP_PAYMENT_PURCHASE_ERROR,
          payload: { productId: product.id, error: { code: 'VERIFY_FAIL' } },
        });
        sendTechEvent('iap.unverified');
      };

      handlers.owned = product => {
        console.info('product owned', JSON.stringify(product));
        dispatch({ type: IN_APP_PAYMENT_PURCHASE_COMPLETE });
        dispatch({ type: IN_APP_PAYMENT_RESTORE_COMPLETE });
      };

      handlers.expired = product => {
        console.info('product expired', JSON.stringify(product));
        dispatch({ type: IN_APP_PAYMENT_PURCHASE_CANCELLED });
        dispatch({ type: IN_APP_PAYMENT_RESTORE_COMPLETE });
      };

      handlers.cancelled = product => {
        console.info('product cancelled', JSON.stringify(product));
        dispatch({ type: IN_APP_PAYMENT_PURCHASE_CANCELLED });
        dispatch({ type: IN_APP_PAYMENT_RESTORE_COMPLETE });
      };

      handlers.ready = () => dispatch({ type: IN_APP_PAYMENT_READY });

      paymentStore.error(handlers.error);
      paymentStore.ready(handlers.ready);

      paymentStore
        .when()
        .productUpdated(handlers.updated)
        .approved(handlers.approved)
        .verified(handlers.verified)
        .receiptUpdated(handlers.owned)
        .finished(handlers.expired);

      // We need to make sure we only call refresh() once, unless the user decides to to a Restore Purchases.
      if (!initialRefreshTriggered) {
        await paymentStore.initialize();
        console.info('paymentStore.initialize() 1 ran.');
        initialRefreshTriggered = true;
      }
    }
  };
}

export function restorePurchases() {
  if (!window.CdvPurchase) {
    console.error('window.CdvPurchase is not defined');
    return;
  }

  const { store: paymentStore } = window.CdvPurchase;

  // eslint-disable-next-line consistent-return
  return async dispatch => {
    if (paymentStore) {
      sendTechEvent('iap.restorePurchases');
      dispatch({ type: IN_APP_PAYMENT_RESTORE_START });
      // https://github.com/j3k0/cordova-plugin-purchase/issues/1050
      // The finished event never seems to fire. To work around this we'll dispatch
      // IN_APP_PAYMENT_RESTORE_COMPLETE from the owned, expired and cancelled handlers too.
      const dispatchComplete = () => dispatch({ type: IN_APP_PAYMENT_RESTORE_COMPLETE });
      // And also hide it after 15 seconds just in case
      const refreshTimeout = setTimeout(
        () =>
          dispatch({ type: IN_APP_PAYMENT_RESTORE_ERROR, payload: { message: 'Refresh timeout' } }),
        15000,
      );
      try {
        // the finished callback doesn't always seem to be there
        await paymentStore.initialize();
        console.info('paymentStore.initialize() ran.');
        dispatchComplete();
        clearTimeout(refreshTimeout);
      } catch (err) {
        // ignore
      }
    }
  };
}

export function updatePrices() {
  if (!window.CdvPurchase) {
    console.error('window.CdvPurchase is not defined');
    return () => {};
  }

  const { store: paymentStore } = window.CdvPurchase;

  return async () => {
    if (paymentStore) {
      await paymentStore.initialize();
      console.info('paymentStore.update() ran.');
    }
  };
}

export function purchase(productId, discount) {
  if (!window.CdvPurchase) {
    console.error('window.CdvPurchase is not defined');
    return () => {};
  }

  const { store: paymentStore } = window.CdvPurchase;

  return async dispatch => {
    if (paymentStore) {
      try {
        const productToOrder = paymentStore.get(productId);

        if (!productToOrder) {
          console.error('Product to order not found');
          return;
        }

        sendTechEvent('iap.start');
        dispatch({ type: IN_APP_PAYMENT_PURCHASE_START, payload: { productId } });

        const offer = await productToOrder.getOffer();

        if (discount) {
          // Add the discount data to the order
          await offer.order({
            appStore: {
              discount: {
                id: discount.id,
                key: discount.key,
                signature: discount.signature,
                timestamp: discount.timestamp,
              },
            },
          });
        } else {
          await offer.order();
        }
      } catch (error) {
        console.error('Could not order subscription', productId, error);
        dispatch({
          type: IN_APP_PAYMENT_PURCHASE_ERROR,
          payload: { productId, error },
        });
        sendTechEvent('iap.purchaseFail', { code: 'ORDER_ERROR_1', error: error.message });
      }
    }
  };
}

export function clearErrors() {
  return { type: IN_APP_PAYMENT_CLEAR_ERRORS, payload: null };
}
